import React from 'react';
import Layout from '../components/Layout';
import BackButton from '../components/common/BackButton';
import logo from '../assets/logo.png';
import './ScanResults.css'; // Borrowing the scan results styles
import './MagazineLayout.css'; // Magazine spread layout

// Arrays removed as per user request

const MissionFile = () => {
    // Random color rotation for visual variety
    const colorMap = ['cyan', 'coral', 'green', 'pink', 'yellow'];

    return (
        <Layout>
            <div className="scan-results-container">
                <header className="results-header">
                    <div className="header-content">
                        <div className="results-header-title">
                            <p className="scan-breadcrumb">Home &gt; Tactical Dossier</p>
                            <h1>Mission File</h1>
                            <p className="scan-last-updated">The philosophy that forged LinkLoad into existence.</p>
                        </div>
                        <div className="header-actions">
                            <button
                                onClick={() => window.open('https://github.com/LinkLoad', '_blank', 'noopener,noreferrer')}
                                className="download-pdf-button"
                            >
                                VIEW REPOSITORY
                            </button>
                            <BackButton to="/" label="RETURN TO BASE" />
                        </div>
                    </div>
                </header>

                <div className="scan-results-workspace">
                <div className="tabs-content">
                    <div className="newspaper-spread" style={{ paddingLeft: '2.5rem', paddingRight: '2.5rem' }}>
                        
                        <div className="np-header-line">
                            <span>VOL. 1 — TACTICAL DOSSIER</span>
                            <span>DECLASSIFIED REPORT</span>
                            <span>CLASSIFICATION: TOP SECRET</span>
                        </div>

                        {/* TOP SECTION: MAIN NEWS ABOUT LINKLOAD */}
                        <div className="np-top-section">
                            <div className="np-main-news">
                                <h1 className="np-huge-headline">THE SIGNAL IN THE NOISE</h1>
                                <h3 className="np-sub-headline">LinkLoad Tactical Command Platform Online</h3>
                                
                                <div className="np-main-body-wrapper">
                                    <div className="np-hero-image-container">
                                        <div className="np-hero-graphic">
                                            <img src={logo} alt="LinkLoad Planet" className="np-hero-logo" />
                                            <h2 className="np-hero-title">LINKLOAD</h2>
                                            <p className="np-hero-subtitle">Link. Load. Defend. Repeat.</p>
                                        </div>
                                    </div>
                                    <div className="np-multi-column-text">
                                        <p className="np-lead-paragraph">The modern reconnaissance battlefield isn't starved for tools—it's drowning in them. Security teams don't lose because they lack scanners; they lose because the noise arrives significantly faster than the signal.</p>
                                        <p>LinkLoad was explicitly engineered as a tactical command platform to solve this exact crisis. Rather than adding another bloated scanner to your arsenal, it acts as the central brain. It seamlessly coordinates specialized scanner units, fusing their raw, chaotic findings with actionable intelligence.</p>
                                        <p>By mapping detected risks directly to the MITRE ATT&CK framework, LinkLoad strips away the false positives and delivers a single, unified operational picture through one intuitive gateway. It turns endless streams of unorganized JSON outputs into a prioritized hitlist.</p>
                                        <p>If your squad requires rapid detection, ruthless prioritization, and absolute clarity under fire, this stack isn't just another tool. It is designed for that exact battlefield. Deploy it, load the targets, and watch the noise fade.</p>
                                    </div>
                                </div>
                            </div>

                            {/* RIGHT SIDEBAR FOR TOP SECTION */}
                            <div className="np-sidebar">
                                <div className="np-section-header">FIELD NOTES</div>
                                
                                <div className="np-sidebar-article">
                                    <div className="np-sidebar-image" style={{ background: 'var(--scan-pink)' }}>
                                        <img src="/target-icon.png" alt="Target Icon" onError={(e) => e.target.style.display = 'none'} />
                                    </div>
                                    <h4>Depth Capped at 15</h4>
                                    <p>Because getting stuck in a HackThisSite calendar loop for 6 hours is a rite of passage we'd like to skip.</p>
                                    <span className="np-author-tag">OPERATOR LOG</span>
                                </div>

                                <div className="np-sidebar-article">
                                    <div className="np-sidebar-image" style={{ background: 'var(--scan-green)' }}>
                                        <img src="/signal-icon.png" alt="Signal Icon" onError={(e) => e.target.style.display = 'none'} />
                                    </div>
                                    <h4>Your ₹10 Lakh Scanner is Just Nmap</h4>
                                    <p>And honestly, we took that personally. So we built our own orchestrator.</p>
                                    <span className="np-author-tag">INDUSTRY RANT</span>
                                </div>
                            </div>
                        </div>

                        <div className="scan-tabs-divider" aria-hidden="true" style={{ margin: '4rem 0' }}></div>

                        {/* BOTTOM SECTION: ORIGIN STORY */}
                        <div className="np-bottom-section">
                            {/* LEFT SIDEBAR FOR BOTTOM SECTION */}
                            <div className="np-sidebar left-side">
                                <div className="np-section-header">TACTICAL BRIEFS</div>
                                
                                <div className="np-sidebar-article">
                                    <h4>Noise vs. Signal</h4>
                                    <p>Security teams rarely lose because they lack tools. They lose because noise arrives faster than signal.</p>
                                </div>
                                <div className="np-sidebar-article">
                                    <h4>The Duct-Tape Engine</h4>
                                    <p>ZAP, Nuclei, Wapiti, and Nikto. Orchestrated, containerized, and pointed at the enemy.</p>
                                </div>
                                <div className="np-sidebar-article">
                                    <h4>Open Source Ethos</h4>
                                    <p>MIT licensed because gatekeeping baseline security tools helps no one but the adversary.</p>
                                </div>
                            </div>

                            <div className="np-origin-story">
                                <div className="np-section-header" style={{ background: 'var(--scan-coral)', color: 'var(--scan-text)' }}>OPERATOR ORIGINS</div>
                                <h2 className="np-massive-headline">I bought a domain before I had a product.</h2>
                                
                                <div className="np-origin-content">
                                    <div className="np-portrait-container">
                                        <img src="/operator-pixel.png" alt="Operator Pixel Art" onError={(e) => e.target.style.display = 'none'} />
                                    </div>
                                    <div className="np-origin-text">
                                        <p>This platform was born out of pure, unadulterated frustration with clunky enterprise scanners. I originally frantically duct-taped together the "Hello World" equivalent of a security tool—and it did absolutely nothing useful.</p>
                                        <p>Somewhere between getting repeatedly ghosted by companies, reading endless API docs, and making highly questionable, late-night domain purchases, this evolved into a serious reconnaissance engine. At the time, I had absolutely no clue I was building a production-grade vulnerability orchestrator (and honestly, some days I still don't).</p>
                                        <p>Is it the most complex Web Security Reconnaissance Platform in the world? Probably not. But is it a fast, practical tool that I actually use every single day? Absolutely. It was built by an operator, for operators—cutting through the noise to deliver exactly the tactical intel you need, without the enterprise bloat.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>

                </div>
                </div>
            </div>
        </Layout>
    );
};

export default MissionFile;
